import { ethers } from 'ethers';
import {
  QccContractChainKey,
  getQccChainConfig,
  isQccChainSupported,
} from '../../constants/qcc-contract.config';

const TOKEN_ABI = [
  'function balanceOf(address account) view returns (uint256)',
  'function getActivePrice() view returns (uint256)',
  'function currentThresholdIndex() view returns (uint256)',
  'function getAllThresholds() view returns (uint256[] thresholds, uint256[] prices)',
  'function soldSupply() view returns (uint256)',
];

export type QccContractSaleStats = {
  currentTier: number;
  activePriceUsd: number | null;
  tierPriceUsd: number | null;
  soldSupply: number | null;
};

export function decodeUsd18(value: ethers.BigNumberish): number | null {
  const usd = parseFloat(ethers.utils.formatUnits(value, 18));
  if (!Number.isFinite(usd) || usd <= 0) return null;
  return usd;
}

const providerCache: Partial<
  Record<QccContractChainKey, ethers.providers.JsonRpcProvider>
> = {};
const tokenContractCache: Partial<
  Record<QccContractChainKey, ethers.Contract>
> = {};

function getReadProvider(chainKey: QccContractChainKey): ethers.providers.JsonRpcProvider {
  const cached = providerCache[chainKey];
  if (cached) return cached;
  const cfg = getQccChainConfig(chainKey);
  const provider = new ethers.providers.JsonRpcProvider(cfg.rpcUrls[0]);
  providerCache[chainKey] = provider;
  return provider;
}

export function getReadTokenContract(chainKey: QccContractChainKey): ethers.Contract {
  const cached = tokenContractCache[chainKey];
  if (cached) return cached;
  const cfg = getQccChainConfig(chainKey);
  if (!isQccChainSupported(chainKey)) {
    throw new Error(`Chain ${chainKey} not deployed`);
  }
  const contract = new ethers.Contract(
    cfg.tokenAddress,
    TOKEN_ABI,
    getReadProvider(chainKey),
  );
  tokenContractCache[chainKey] = contract;
  return contract;
}

export function formatQcc(wei: ethers.BigNumberish, decimals = 2): string {
  const formatted = ethers.utils.formatUnits(wei, 18);
  const [whole, frac = ''] = formatted.split('.');
  return `${whole}.${frac.slice(0, decimals).padEnd(decimals, '0')}`;
}

export async function getQccBalance(
  chainKey: QccContractChainKey,
  userAddress: string,
): Promise<ethers.BigNumber> {
  try {
    if (!isQccChainSupported(chainKey)) {
      return ethers.BigNumber.from(0);
    }
    const token = getReadTokenContract(chainKey);
    return await token.balanceOf(userAddress);
  } catch (err) {
    console.warn(`Failed to fetch QCC balance on ${chainKey}`, err);
    return ethers.BigNumber.from(0);
  }
}

export async function getActiveCoinPriceUsd(
  chainKey: QccContractChainKey,
): Promise<number | null> {
  try {
    if (!isQccChainSupported(chainKey)) {
      return null;
    }
    const token = getReadTokenContract(chainKey);
    const price = await token.getActivePrice();
    return decodeUsd18(price);
  } catch (err) {
    console.warn(`Failed to fetch QCC active price on ${chainKey}`, err);
    return null;
  }
}

export async function getContractSaleStats(
  chainKey: QccContractChainKey,
): Promise<QccContractSaleStats | null> {
  try {
    if (!isQccChainSupported(chainKey)) {
      return null;
    }
    const token = getReadTokenContract(chainKey);
    const [activePrice, currentTierBN, soldSupply, thresholdsRet] = await Promise.all([
      token.getActivePrice(),
      token.currentThresholdIndex(),
      token.soldSupply(),
      token.getAllThresholds(),
    ]);
    const currentTier = currentTierBN.toNumber();
    const prices = thresholdsRet.prices || thresholdsRet[1] || [];
    const sold = parseFloat(ethers.utils.formatUnits(soldSupply, 18));
    return {
      currentTier,
      activePriceUsd: decodeUsd18(activePrice),
      tierPriceUsd: prices[currentTier] != null ? decodeUsd18(prices[currentTier]) : null,
      soldSupply: Number.isFinite(sold) ? sold : null,
    };
  } catch (err) {
    console.warn(`Failed to fetch QCC sale stats on ${chainKey}`, err);
    return null;
  }
}

export function resetQccContractCachesForTests(): void {
  Object.keys(providerCache).forEach(key => {
    delete providerCache[key as QccContractChainKey];
  });
  Object.keys(tokenContractCache).forEach(key => {
    delete tokenContractCache[key as QccContractChainKey];
  });
}
